/**
 * xlsxTemplates — domain-specific builders that turn app data into XlsxSheet[]
 * definitions consumed by exportToXlsx. Kept free of React/hook imports so the
 * lazily-loaded exceljs chunk stays lean and the builders remain unit-testable.
 */
import type { XlsxSheet } from './xlsxExport';
import { STOCK_REASON_LABELS } from './stock';
import { alertKindOf, balanceOf, STOCK_ALERT_LABELS } from './stockReport';
import { productUnitCost } from './cost';
import { unitLabel as centralUnitLabel } from './units';
import { ADHOC_ITEM_BADGE, ITEM_KIND_LABELS } from './itemsReport';
import type { StockMovementRow, StockUnitRow } from './stockReport';
import type { ItemsReport } from './itemsReport';
import type {
  ContractsReport,
  ReceiptsReport,
  ServiceOrdersReport,
} from './documentsReport';

// Row shapes are intentionally minimal/loose so both the full domain types
// (Quote, Client, Product, FinanceEntryWithStatus) and the trimmed projections
// used on some pages (e.g. ReportsPage's QuoteData) satisfy them structurally.

export interface QuoteRow {
  sequence_id?: number | null;
  title?: string | null;
  client_name?: string | null;
  status: string;
  created_at: string;
  validity_days?: number | null;
  total: number;
}

export interface QuoteItemRow {
  quote_id: string;
  name: string;
  quantity: number;
  unit?: string | null;
  unit_price: number;
  discount?: number | null;
  total: number;
}

export interface ClientRow {
  id: string;
  name: string;
  phone?: string | null;
  address?: string | null;
  created_at: string;
}

export interface ClientQuoteRow {
  client_id?: string | null;
  status: string;
  total: number;
}

export interface ProductRow {
  id?: string;
  name: string;
  description?: string | null;
  price: number;
  type?: 'service' | 'product' | null;
  unit?: string | null;
  category?: string | null;
  /** Preenchido quando a linha é uma VARIAÇÃO de outro produto. */
  parent_product_id?: string | null;
  /** Rótulo da variação ("P", "Azul 500ml"). */
  variant_name?: string | null;
  /** Custo de compra/produção. `null`/ausente = não informado (não é zero). */
  cost?: number | null;
}

export interface FinanceEntryRow {
  kind: 'income' | 'expense';
  description: string;
  amount: number;
  due_date: string;
  paid_date?: string | null;
  status: string;
  computed_status?: string | null;
  payment_method?: string | null;
  recurrence?: string | null;
  category_id?: string | null;
}

export interface FinanceCategoryRow {
  id: string;
  name: string;
  kind: 'income' | 'expense';
}

// ===== Shared label maps =====

const QUOTE_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  approved: 'Aprovado',
  completed: 'Concluído',
  cancelled: 'Cancelado',
  rejected: 'Rejeitado',
};

const FINANCE_STATUS_LABELS: Record<string, string> = {
  pending: 'Pendente',
  paid: 'Pago',
  overdue: 'Vencido',
  canceled: 'Cancelado',
};

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: 'Dinheiro',
  pix: 'PIX',
  card_credit: 'Cartão de crédito',
  card_debit: 'Cartão de débito',
  boleto: 'Boleto',
  transfer: 'Transferência',
  other: 'Outro',
};

const RECURRENCE_LABELS: Record<string, string> = {
  none: 'Não repetir',
  weekly: 'Semanal',
  monthly: 'Mensal',
  yearly: 'Anual',
};

// ===== Helpers =====

/**
 * Convert an app date value to a Date object for exceljs date cells.
 * Plain 'YYYY-MM-DD' strings are anchored to local midnight to avoid the
 * timezone drift that `new Date('YYYY-MM-DD')` (UTC) would introduce.
 */
function toDate(value?: string | null): Date | null {
  if (!value) return null;
  const str = String(value);
  const normalized = /^\d{4}-\d{2}-\d{2}$/.test(str) ? `${str}T00:00:00` : str;
  const d = new Date(normalized);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** validade = created_at + validity_days, expressed as an expiration date. */
function validityDate(createdAt: string, validityDays?: number | null): Date | null {
  if (validityDays == null) return null;
  const base = toDate(createdAt);
  if (!base) return null;
  const d = new Date(base);
  d.setDate(d.getDate() + validityDays);
  return d;
}

/**
 * Rótulo da unidade pelo vocabulário ÚNICO (src/lib/units.ts).
 *
 * Antes havia dois mapas locais que só conheciam o vocabulário do CATÁLOGO, e
 * um item de ORÇAMENTO (que usa o outro vocabulário) saía na planilha com o
 * código cru: "h" em vez de "Hora (h)", "lauda" em vez de "Lauda".
 */
function unitLabel(unit: string | null | undefined, type: 'service' | 'product' | null | undefined): string {
  return centralUnitLabel(unit, type ?? undefined);
}

// ===== Quotes =====

/**
 * "Orçamentos" sheet, plus an optional "Itens" sheet when line items are
 * provided. brandColor flows through to exportToXlsx for header styling.
 */
export function buildQuotesXlsx(
  quotes: QuoteRow[],
  items?: QuoteItemRow[],
  _companyName?: string,
  _brandColor?: string,
): XlsxSheet[] {
  const sheets: XlsxSheet[] = [
    {
      name: 'Orçamentos',
      columns: [
        { header: 'Número', key: 'numero', width: 10, format: 'text' },
        { header: 'Título', key: 'titulo', width: 28, format: 'text' },
        { header: 'Cliente', key: 'cliente', width: 24, format: 'text' },
        { header: 'Status', key: 'status', width: 14, format: 'text' },
        { header: 'Data', key: 'data', width: 14, format: 'date' },
        { header: 'Validade', key: 'validade', width: 14, format: 'date' },
        { header: 'Total', key: 'total', width: 16, format: 'currency' },
      ],
      rows: quotes.map((q) => ({
        numero: q.sequence_id != null ? `#${String(q.sequence_id).padStart(4, '0')}` : '',
        titulo: q.title || '',
        cliente: q.client_name || 'Sem cliente',
        status: QUOTE_STATUS_LABELS[q.status] || q.status,
        data: toDate(q.created_at),
        validade: validityDate(q.created_at, q.validity_days),
        total: q.total ?? 0,
      })),
    },
  ];

  if (items && items.length > 0) {
    sheets.push({
      name: 'Itens',
      columns: [
        { header: 'Orçamento', key: 'quote_id', width: 24, format: 'text' },
        { header: 'Nome', key: 'nome', width: 30, format: 'text' },
        // Quantidade de item é FRACIONÁRIA (1,5 kg / 2,5 h). Com 'integer'
        // (numFmt '0') o Excel arredondaria 1,5 para 2 na exibição — a planilha
        // mentiria sobre a venda sem nenhum erro.
        { header: 'Qtd', key: 'qtd', width: 8, format: 'decimal' },
        { header: 'Unidade', key: 'unidade', width: 14, format: 'text' },
        { header: 'Preço Unit.', key: 'unit_price', width: 16, format: 'currency' },
        { header: 'Desconto', key: 'desconto', width: 14, format: 'currency' },
        { header: 'Total', key: 'total', width: 16, format: 'currency' },
      ],
      rows: items.map((it) => ({
        quote_id: it.quote_id,
        nome: it.name,
        qtd: it.quantity ?? 0,
        unidade: it.unit || '',
        unit_price: it.unit_price ?? 0,
        desconto: it.discount ?? 0,
        total: it.total ?? 0,
      })),
    });
  }

  return sheets;
}

// ===== Clients =====

/**
 * "Clientes" sheet. Total spent is computed by joining the optional quotes list
 * on client_id and summing approved + completed totals.
 */
export function buildClientsXlsx(
  clients: ClientRow[],
  quotes?: ClientQuoteRow[],
): XlsxSheet[] {
  const spentByClient = new Map<string, number>();
  for (const q of quotes ?? []) {
    if (!q.client_id) continue;
    if (q.status === 'approved' || q.status === 'completed') {
      spentByClient.set(q.client_id, (spentByClient.get(q.client_id) ?? 0) + (q.total || 0));
    }
  }

  return [
    {
      name: 'Clientes',
      columns: [
        { header: 'Nome', key: 'nome', width: 28, format: 'text' },
        { header: 'Telefone', key: 'telefone', width: 18, format: 'text' },
        { header: 'Endereço', key: 'endereco', width: 34, format: 'text' },
        { header: 'Criado em', key: 'criado_em', width: 14, format: 'date' },
        { header: 'Total gasto', key: 'total_gasto', width: 16, format: 'currency' },
      ],
      rows: clients.map((c) => ({
        nome: c.name,
        telefone: c.phone || '',
        endereco: c.address || '',
        criado_em: toDate(c.created_at),
        total_gasto: spentByClient.get(c.id) ?? 0,
      })),
    },
  ];
}

// ===== Catalog =====

/**
 * "Serviços" and "Produtos" sheets, split by product type.
 *
 * Variations are rows of their own (they are the sellable unit), with a
 * "Produto pai" column tying them back to their parent — the flat product list
 * mixes parents and children, so without that column a spreadsheet of
 * "Camiseta / Camiseta / Camiseta" would be unreadable.
 */
export function buildCatalogXlsx(products: ProductRow[]): XlsxSheet[] {
  const columns = [
    { header: 'Nome', key: 'nome', width: 30, format: 'text' as const },
    { header: 'Produto pai', key: 'produto_pai', width: 24, format: 'text' as const },
    { header: 'Variação', key: 'variacao', width: 16, format: 'text' as const },
    { header: 'Descrição', key: 'descricao', width: 40, format: 'text' as const },
    { header: 'Unidade', key: 'unidade', width: 18, format: 'text' as const },
    { header: 'Categoria', key: 'categoria', width: 20, format: 'text' as const },
    { header: 'Preço', key: 'preco', width: 16, format: 'currency' as const },
    // Coluna NOVA no fim de propósito: quem já usa a planilha não quer as
    // colunas antigas trocando de lugar. Célula VAZIA quando não há custo — 0
    // significaria "de graça".
    { header: 'Custo', key: 'custo', width: 16, format: 'currency' as const },
  ];

  // Nome do pai por id, para a coluna "Produto pai". Se o pai não veio na lista
  // (seleção parcial), cai no `name` da própria variação, que já é o do pai.
  const nameById = new Map<string, string>();
  for (const p of products) {
    if (p.id) nameById.set(String(p.id), p.name);
  }

  const toRow = (p: ProductRow) => {
    const parentName = p.parent_product_id
      ? nameById.get(String(p.parent_product_id)) ?? p.name
      : '';
    return {
      // A variação aparece como "Camiseta — P" para a linha se explicar sozinha.
      nome: p.variant_name ? `${parentName || p.name} — ${p.variant_name}` : p.name,
      produto_pai: parentName,
      variacao: p.variant_name || '',
      descricao: p.description || '',
      unidade: unitLabel(p.unit, p.type),
      categoria: p.category || '',
      preco: p.price ?? 0,
      custo: productUnitCost(p),
    };
  };

  const services = products.filter((p) => (p.type ?? 'service') === 'service');
  const goods = products.filter((p) => p.type === 'product');

  return [
    { name: 'Serviços', columns, rows: services.map(toRow) },
    { name: 'Produtos', columns, rows: goods.map(toRow) },
  ];
}

// ===== Stock =====

/**
 * "Posição" (fotografia de agora) + "Movimentações" (a razão do período).
 *
 * Duas escolhas deliberadas, ambas para a planilha não mentir:
 *
 * - A coluna de valor se chama **"Potencial de venda"**, não "valor em estoque":
 *   `products` não tem custo, só preço de VENDA. Chamar `saldo × price` de
 *   "valor em estoque" seria informar receita potencial como capital imobilizado.
 * - As linhas são **unidades estocáveis** (a variação, nunca o pai somado), com
 *   a coluna "Variação" dando o contexto — o chamador já filtra com
 *   `isStockableUnit`.
 *
 * A coluna "Situação" usa `alertKindOf`, a mesma função dos baldes de alerta da
 * tela, então planilha e relatório nunca divergem.
 */
export function buildStockXlsx(
  units: StockUnitRow[],
  movements: StockMovementRow[],
): XlsxSheet[] {
  const posicao: XlsxSheet = {
    name: 'Posição',
    columns: [
      { header: 'Produto', key: 'produto', width: 30, format: 'text' },
      { header: 'Variação', key: 'variacao', width: 16, format: 'text' },
      { header: 'Categoria', key: 'categoria', width: 20, format: 'text' },
      { header: 'Unidade', key: 'unidade', width: 12, format: 'text' },
      { header: 'Saldo', key: 'saldo', width: 12, format: 'decimal' },
      { header: 'Mínimo', key: 'minimo', width: 12, format: 'decimal' },
      { header: 'Situação', key: 'situacao', width: 20, format: 'text' },
      { header: 'Preço de venda', key: 'preco', width: 16, format: 'currency' },
      { header: 'Potencial de venda', key: 'potencial', width: 20, format: 'currency' },
      // Colunas NOVAS no fim: as antigas não mudam de lugar para não quebrar a
      // planilha de quem já usa.
      { header: 'Custo', key: 'custo', width: 16, format: 'currency' },
      { header: 'Valor investido', key: 'investido', width: 18, format: 'currency' },
    ],
    rows: (units ?? []).map((u) => {
      const balance = balanceOf(u);
      const kind = alertKindOf(u);
      const price = Number(u.price ?? 0) || 0;
      const cost = productUnitCost(u);
      return {
        produto: u.name || 'Produto',
        variacao: u.variant_name || '',
        categoria: u.category || '',
        unidade: u.unit || '',
        saldo: balance,
        // Mínimo NULO é "sem alerta", não zero — a célula fica vazia.
        minimo: u.min_stock == null ? null : Number(u.min_stock),
        situacao: kind ? STOCK_ALERT_LABELS[kind] : 'OK',
        preco: price,
        // Saldo negativo não vira potencial de venda negativo: não há mercadoria.
        potencial: balance > 0 ? balance * price : 0,
        // Sem custo informado a célula fica VAZIA (não 0): quem soma a coluna na
        // planilha precisa ver que aquele item não entrou na conta.
        custo: cost,
        investido: cost == null ? null : balance > 0 ? balance * cost : 0,
      };
    }),
  };

  // Unidade por id, para a razão não virar uma lista de UUIDs. Um movimento de
  // produto já excluído continua na planilha (o total tem que fechar), rotulado
  // como removido.
  const unitById = new Map<string, StockUnitRow>();
  for (const u of units ?? []) unitById.set(String(u.id), u);

  const movimentacoes: XlsxSheet = {
    name: 'Movimentações',
    columns: [
      { header: 'Data', key: 'data', width: 18, format: 'datetime' },
      { header: 'Produto', key: 'produto', width: 30, format: 'text' },
      { header: 'Variação', key: 'variacao', width: 16, format: 'text' },
      { header: 'Motivo', key: 'motivo', width: 28, format: 'text' },
      { header: 'Tipo', key: 'tipo', width: 12, format: 'text' },
      { header: 'Quantidade', key: 'quantidade', width: 14, format: 'decimal' },
      { header: 'Saldo após', key: 'saldo_apos', width: 14, format: 'decimal' },
      { header: 'Observação', key: 'observacao', width: 34, format: 'text' },
    ],
    rows: (movements ?? []).map((m) => {
      const unit = unitById.get(String(m.product_id));
      const delta = Number(m.delta ?? 0) || 0;
      return {
        data: toDate(m.created_at),
        produto: unit?.name || 'Produto removido',
        variacao: unit?.variant_name || '',
        motivo: STOCK_REASON_LABELS[m.reason] || m.reason,
        // O sinal do DELTA decide, nunca o motivo: 'adjustment' pode subir ou
        // descer o saldo.
        tipo: delta >= 0 ? 'Entrada' : 'Saída',
        quantidade: delta,
        saldo_apos: m.balance_after == null ? null : Number(m.balance_after),
        observacao: m.note || '',
      };
    }),
  };

  return [posicao, movimentacoes];
}

// ===== Itens vendidos =====

/**
 * "Itens" (o que foi vendido, com margem) + "Clientes" (margem por cliente).
 *
 * Célula VAZIA, nunca 0, quando não há custo informado — 0 significaria
 * "custou nada" e quem soma a coluna precisa ver que aquela linha não entrou na
 * conta. É a mesma regra da coluna Custo do catálogo e do estoque (#61).
 */
export function buildItemsXlsx(report: ItemsReport): XlsxSheet[] {
  return [
    {
      name: 'Itens',
      columns: [
        { header: 'Item', key: 'item', width: 32, format: 'text' },
        { header: 'Origem', key: 'origem', width: 14, format: 'text' },
        { header: 'Tipo', key: 'tipo', width: 12, format: 'text' },
        { header: 'Unidade', key: 'unidade', width: 12, format: 'text' },
        { header: 'Quantidade', key: 'quantidade', width: 14, format: 'decimal' },
        { header: 'Linhas', key: 'linhas', width: 10, format: 'integer' },
        { header: 'Documentos', key: 'documentos', width: 12, format: 'integer' },
        { header: 'Receita', key: 'receita', width: 16, format: 'currency' },
        { header: 'Custo', key: 'custo', width: 16, format: 'currency' },
        { header: 'Margem', key: 'margem', width: 16, format: 'currency' },
        { header: 'Margem %', key: 'margem_pct', width: 12, format: 'percent' },
        { header: 'Linhas sem custo', key: 'sem_custo', width: 16, format: 'integer' },
      ],
      rows: report.items.map((item) => ({
        item: item.name,
        origem: item.productId ? 'Catálogo' : ADHOC_ITEM_BADGE,
        tipo: ITEM_KIND_LABELS[item.kind],
        unidade: item.unit || '',
        quantidade: item.quantity,
        linhas: item.lines,
        documentos: item.documents,
        receita: item.revenue,
        // Sem NENHUMA linha com custo, custo e margem ficam vazios.
        custo: item.margin == null ? null : item.cost,
        margem: item.margin,
        // Fração — o formato 'percent' do Excel já multiplica por 100.
        margem_pct: item.marginPercent == null ? null : item.marginPercent / 100,
        sem_custo: item.missingCostLines,
      })),
    },
    {
      name: 'Clientes',
      columns: [
        { header: 'Cliente', key: 'cliente', width: 28, format: 'text' },
        { header: 'Documentos', key: 'documentos', width: 12, format: 'integer' },
        { header: 'Receita', key: 'receita', width: 16, format: 'currency' },
        { header: 'Custo', key: 'custo', width: 16, format: 'currency' },
        { header: 'Margem', key: 'margem', width: 16, format: 'currency' },
        { header: 'Margem %', key: 'margem_pct', width: 12, format: 'percent' },
        { header: 'Linhas sem custo', key: 'sem_custo', width: 16, format: 'integer' },
      ],
      rows: report.clients.map((c) => ({
        cliente: c.name,
        documentos: c.documents,
        receita: c.revenue,
        custo: c.margin == null ? null : c.cost,
        margem: c.margin,
        margem_pct: c.marginPercent == null ? null : c.marginPercent / 100,
        sem_custo: c.missingCostLines,
      })),
    },
  ];
}

// ===== Documentos (OS, contratos, recibos) =====

/**
 * "Ordens de serviço" + "Contratos" + "Recibos".
 *
 * A coluna "De orçamento" das OS existe porque a aba Lucro PULA essas ordens no
 * CMV (o custo já está no orçamento de origem) — sem a coluna, quem confere a
 * planilha contra a DRE conclui que uma das duas está errada.
 *
 * Recibo não tem coluna `status` no banco (#57): "Situação" sai de
 * `cancelled_at`, pela mesma função que a tela usa.
 */
export function buildDocumentsXlsx(
  orders: ServiceOrdersReport,
  contracts: ContractsReport,
  receipts: ReceiptsReport,
): XlsxSheet[] {
  return [
    {
      name: 'Ordens de serviço',
      columns: [
        { header: 'Data', key: 'data', width: 14, format: 'date' },
        { header: 'Título', key: 'titulo', width: 30, format: 'text' },
        { header: 'Cliente', key: 'cliente', width: 24, format: 'text' },
        { header: 'Status', key: 'status', width: 16, format: 'text' },
        { header: 'De orçamento', key: 'de_orcamento', width: 14, format: 'text' },
        { header: 'Total', key: 'total', width: 16, format: 'currency' },
        { header: 'Custo', key: 'custo', width: 16, format: 'currency' },
        { header: 'Linhas sem custo', key: 'sem_custo', width: 16, format: 'integer' },
      ],
      rows: orders.rows.map((row) => ({
        data: toDate(row.date),
        titulo: row.title,
        cliente: row.clientName,
        status: row.statusLabel,
        de_orcamento: row.fromQuote ? 'Sim' : 'Não',
        total: row.total,
        custo: row.cost,
        sem_custo: row.missingCostLines,
      })),
    },
    {
      name: 'Contratos',
      columns: [
        { header: 'Data', key: 'data', width: 14, format: 'date' },
        { header: 'Título', key: 'titulo', width: 30, format: 'text' },
        { header: 'Cliente', key: 'cliente', width: 24, format: 'text' },
        { header: 'Status', key: 'status', width: 16, format: 'text' },
        { header: 'Total', key: 'total', width: 16, format: 'currency' },
      ],
      rows: contracts.rows.map((row) => ({
        data: toDate(row.date),
        titulo: row.title,
        cliente: row.clientName,
        status: row.statusLabel,
        total: row.total,
      })),
    },
    {
      name: 'Recibos',
      columns: [
        { header: 'Emissão', key: 'emissao', width: 14, format: 'date' },
        { header: 'Pagador', key: 'pagador', width: 28, format: 'text' },
        { header: 'Situação', key: 'situacao', width: 14, format: 'text' },
        { header: 'Forma', key: 'forma', width: 18, format: 'text' },
        { header: 'Valor', key: 'valor', width: 16, format: 'currency' },
      ],
      rows: receipts.rows.map((row) => ({
        emissao: toDate(row.date),
        pagador: row.payerName,
        situacao: row.stateLabel,
        forma: row.paymentMethod
          ? PAYMENT_METHOD_LABELS[row.paymentMethod] || row.paymentMethod
          : '',
        valor: row.amount,
      })),
    },
  ];
}

// ===== Finance =====

export interface FinanceXlsxOptions {
  /**
   * Coluna de data usada para recortar o período e agrupar o "Resumo":
   * `due` (vencimento, padrão — comportamento histórico do módulo financeiro) ou
   * `paid` (data do pagamento, usado pelo Relatório de Pagamentos).
   */
  basis?: 'due' | 'paid';
  /** Acrescenta a aba "Formas de pagamento" (recebimentos por método). */
  includePaymentMethods?: boolean;
}

/**
 * "Movimentações" + "Categorias" (with per-category totals) + "Resumo"
 * (monthly income/expense/balance pivot). `from`/`to` are optional 'YYYY-MM-DD'
 * bounds filtered against the entry due_date (or paid_date, see
 * `options.basis`).
 */
export function buildFinanceXlsx(
  entries: FinanceEntryRow[],
  categories: FinanceCategoryRow[],
  from?: string,
  to?: string,
  options: FinanceXlsxOptions = {},
): XlsxSheet[] {
  const basis = options.basis ?? 'due';
  // Data que ancora a linha no período. Na base `paid`, quem não tem pagamento
  // fica fora — não é tratado como pago hoje.
  const basisDate = (e: FinanceEntryRow): string | null =>
    (basis === 'paid' ? e.paid_date : e.due_date) ?? null;

  const scoped = entries.filter((e) => {
    const date = basisDate(e);
    // Na base `paid` a ausência de data exclui a linha. Na base `due` a coluna é
    // NOT NULL — não filtramos por ausência, para não mudar o comportamento
    // histórico da exportação do módulo financeiro.
    if (!date) return basis !== 'paid';
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  });

  const categoryName = new Map<string, string>();
  categories.forEach((c) => categoryName.set(c.id, c.name));

  // Movimentações
  const movimentacoes: XlsxSheet = {
    name: 'Movimentações',
    columns: [
      { header: 'Tipo', key: 'tipo', width: 12, format: 'text' },
      { header: 'Descrição', key: 'descricao', width: 32, format: 'text' },
      { header: 'Categoria', key: 'categoria', width: 20, format: 'text' },
      { header: 'Valor', key: 'valor', width: 16, format: 'currency' },
      { header: 'Vencimento', key: 'vencimento', width: 14, format: 'date' },
      { header: 'Pagamento', key: 'pagamento', width: 14, format: 'date' },
      { header: 'Status', key: 'status', width: 14, format: 'text' },
      { header: 'Forma', key: 'forma', width: 18, format: 'text' },
      { header: 'Recorrência', key: 'recorrencia', width: 14, format: 'text' },
    ],
    rows: scoped.map((e) => {
      const status = (e.computed_status ?? e.status) as string;
      return {
        tipo: e.kind === 'income' ? 'Entrada' : 'Saída',
        descricao: e.description,
        categoria: e.category_id ? categoryName.get(e.category_id) || 'Sem categoria' : 'Sem categoria',
        valor: e.amount ?? 0,
        vencimento: toDate(e.due_date),
        pagamento: toDate(e.paid_date),
        status: FINANCE_STATUS_LABELS[status] || status,
        forma: e.payment_method ? PAYMENT_METHOD_LABELS[e.payment_method] || e.payment_method : '',
        recorrencia: e.recurrence ? RECURRENCE_LABELS[e.recurrence] || e.recurrence : '',
      };
    }),
  };

  // Categorias (with per-category totals — the "por categoria" pivot)
  const totalByCategory = new Map<string, number>();
  for (const e of scoped) {
    const key = e.category_id ?? '__none__';
    totalByCategory.set(key, (totalByCategory.get(key) ?? 0) + (e.amount ?? 0));
  }
  const categoriasSheet: XlsxSheet = {
    name: 'Categorias',
    columns: [
      { header: 'Categoria', key: 'categoria', width: 24, format: 'text' },
      { header: 'Tipo', key: 'tipo', width: 12, format: 'text' },
      { header: 'Total', key: 'total', width: 16, format: 'currency' },
    ],
    rows: categories.map((c) => ({
      categoria: c.name,
      tipo: c.kind === 'income' ? 'Entrada' : 'Saída',
      total: totalByCategory.get(c.id) ?? 0,
    })),
  };
  const uncategorized = totalByCategory.get('__none__');
  if (uncategorized) {
    categoriasSheet.rows.push({ categoria: 'Sem categoria', tipo: '', total: uncategorized });
  }

  // Resumo (por mês — na mesma base de data usada no recorte do período)
  const byMonth = new Map<string, { income: number; expense: number }>();
  for (const e of scoped) {
    const month = (basisDate(e) ?? '').slice(0, 7); // YYYY-MM
    if (!month) continue;
    const m = byMonth.get(month) ?? { income: 0, expense: 0 };
    if (e.kind === 'income') m.income += e.amount ?? 0;
    else m.expense += e.amount ?? 0;
    byMonth.set(month, m);
  }
  const resumoSheet: XlsxSheet = {
    name: 'Resumo',
    columns: [
      { header: 'Mês', key: 'mes', width: 12, format: 'text' },
      { header: 'Entradas', key: 'entradas', width: 16, format: 'currency' },
      { header: 'Saídas', key: 'saidas', width: 16, format: 'currency' },
      { header: 'Saldo', key: 'saldo', width: 16, format: 'currency' },
    ],
    rows: Array.from(byMonth.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, v]) => ({
        mes: month,
        entradas: v.income,
        saidas: v.expense,
        saldo: v.income - v.expense,
      })),
  };

  const sheets = [movimentacoes, categoriasSheet, resumoSheet];

  // Formas de pagamento (recebimentos por método) — recorte que dá identidade ao
  // Relatório de Pagamentos. `payment_method` nulo vira "Não informado": nunca
  // descartado nem somado em "Outro", que é um método REAL.
  if (options.includePaymentMethods) {
    const byMethod = new Map<string, { total: number; count: number }>();
    for (const e of scoped) {
      if (e.kind !== 'income' || e.status !== 'paid') continue;
      const key = e.payment_method || '__unspecified__';
      const bucket = byMethod.get(key) ?? { total: 0, count: 0 };
      bucket.total += e.amount ?? 0;
      bucket.count += 1;
      byMethod.set(key, bucket);
    }
    const grandTotal = Array.from(byMethod.values()).reduce((sum, b) => sum + b.total, 0);

    sheets.push({
      name: 'Formas de pagamento',
      columns: [
        { header: 'Forma', key: 'forma', width: 24, format: 'text' },
        { header: 'Recebido', key: 'recebido', width: 16, format: 'currency' },
        { header: 'Lançamentos', key: 'lancamentos', width: 14, format: 'integer' },
        { header: '% do total', key: 'percentual', width: 12, format: 'percent' },
      ],
      rows: Array.from(byMethod.entries())
        .sort(([, a], [, b]) => b.total - a.total)
        .map(([key, b]) => ({
          forma:
            key === '__unspecified__'
              ? 'Não informado'
              : PAYMENT_METHOD_LABELS[key] || key,
          recebido: b.total,
          lancamentos: b.count,
          // Fração (o formato 'percent' do Excel já multiplica por 100).
          // Guarda de divisão por zero: sem recebimento algum o percentual é 0.
          percentual: grandTotal > 0 ? b.total / grandTotal : 0,
        })),
    });
  }

  return sheets;
}
